import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingIndicator, Switch } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertNever, type MandateProps } from '@gitbook/util-ts';

import { createPalette, PaletteButton } from '../Palette';
import { DebouncedTextInput } from './DebouncedTextInput';
import { FormButton } from './FormButton';
import { Picker, type PickerItemData } from './Picker';

interface FormSchemaGenericType {
    title?: string;
    description?: string;
}

interface FormSchemaObject extends FormSchemaGenericType {
    type: 'object';
    properties: {
        [key: string]: FormSchemaPrimitiveType;
    };
    required?: string[];
}

interface FormSchemaString extends FormSchemaGenericType {
    type: 'string';
    default?: string;
    completion_url?: string;
    enum?: string[];
}

interface FormSchemaNumber extends FormSchemaGenericType {
    type: 'number';
    default?: number;
}

interface FormSchemaBoolean extends FormSchemaGenericType {
    type: 'boolean';
    default?: boolean;
}

interface FormSchemaButton extends FormSchemaGenericType {
    type: 'button';
    callback_url: string;
    button_text: string;
}

type FormSchemaPrimitiveType =
    | FormSchemaString
    | FormSchemaNumber
    | FormSchemaBoolean
    | FormSchemaButton;

type FormSchemaType = FormSchemaPrimitiveType | FormSchemaObject;

type FormSchemaValue<Type extends FormSchemaType> = Type extends FormSchemaString
    ? string
    : Type extends FormSchemaNumber
      ? number
      : Type extends FormSchemaBoolean
        ? boolean
        : Type extends FormSchemaButton
          ? any
          : Type extends FormSchemaObject
            ? FormSchemaValueObject<Type>
            : never;

type FormSchemaValueObject<Type extends FormSchemaObject> = {
    [Key in keyof Type['properties']]: FormSchemaValue<Type['properties'][Key]>;
};

interface FormSchemaFieldProps<Schema extends FormSchemaType> {
    propertyKey: string;
    schema: Schema;
    value: FormSchemaValue<Schema>;
    onChangeValue: (value: FormSchemaValue<Schema>) => void;
}

export type FormSchemaOptionalProps = Partial<{
    onFetchResource: (resourceUrl: string) => Promise<Array<PickerItemData>>;
    onNavigationTo: (navigationUrl: string) => void;
}>;

export type FormSchemaProps<Schema extends FormSchemaType> = {
    schema: Schema;
    value: FormSchemaValue<Schema>;
    onChangeValue: (value: FormSchemaValue<Schema>) => void;
} & FormSchemaOptionalProps;

/**
 * Render a JSON schema as a form.
 */
export function FormSchema<Schema extends FormSchemaObject>(
    props: FormSchemaProps<Schema>
): React.ReactElement {
    const { schema, value, onChangeValue, ...rest } = props;

    const keys = [...new Set([...(schema.required || []), ...Object.keys(schema.properties)])];

    return (
        <div>
            {keys.map((key, index) => {
                const property = schema.properties[key];

                return (
                    <div key={key} className={index < keys.length - 1 ? 'mb-6' : undefined}>
                        <FormSchemaPrimitive
                            schema={property}
                            value={value[key]}
                            // @ts-ignore
                            globalValue={value}
                            onChangeValue={(propValue) => {
                                onChangeValue({
                                    ...value,
                                    [key]: propValue,
                                });
                            }}
                            {...rest}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function FormSchemaPrimitive<Schema extends FormSchemaPrimitiveType>(
    props: FormSchemaFieldProps<Schema>
): React.ReactElement {
    const { propertyKey, schema, value, onChangeValue } = props;

    const label = schema.title || propertyKey;
    const labelDescription = schema.description;

    switch (schema.type) {
        case 'string':
            if (schema.enum) {
                // @ts-ignore
                return <FormSchemaWithEnumView {...props} />;
            }

            if (schema.completion_url) {
                // @ts-ignore
                return <FormSchemaWithEnumCompletion {...props} />;
            }

            return (
                <DebouncedTextInput
                    label={label}
                    labelDescription={labelDescription}
                    value={withDefaultValue(value as string, schema.default, '')}
                    onChangeText={onChangeValue}
                    placeholder={schema.default || ''}
                />
            );
        case 'number':
            return (
                <DebouncedTextInput
                    label={schema.title || propertyKey}
                    labelDescription={labelDescription}
                    value={`${withDefaultValue(value as number, schema.default, 0)}`}
                    onChangeText={() => {
                        // @ts-ignore
                        onChangeValue(Number.parseInt(value, 10));
                    }}
                />
            );
        case 'boolean':
            return (
                <Switch
                    label={schema.title || propertyKey}
                    labelDescription={labelDescription}
                    value={withDefaultValue(value as boolean, schema.default, false)}
                    onValueChange={onChangeValue}
                />
            );
        case 'button':
            // @ts-ignore
            return <FormSchemaButtonView {...props} />;
        default:
            assertNever(schema);
    }
}

function FormSchemaButtonView(
    props: FormSchemaFieldProps<FormSchemaButton> &
        MandateProps<FormSchemaOptionalProps, 'onNavigationTo'>
): React.ReactElement {
    const { value, schema, propertyKey, onChangeValue, onNavigationTo } = props;

    return (
        <FormButton label={schema.title || propertyKey} labelDescription={schema.description}>
            {value ? (
                <Button
                    label="Clear"
                    kind="secondary"
                    leadingIcon={Icon.X}
                    onPress={() => {
                        onChangeValue(undefined);
                    }}
                />
            ) : (
                <Button
                    label={schema.button_text}
                    kind="secondary"
                    onPress={() => {
                        onNavigationTo(schema.callback_url);
                    }}
                />
            )}
        </FormButton>
    );
}

function FormSchemaWithEnumView(
    props: FormSchemaFieldProps<MandateProps<FormSchemaString, 'enum'>>
): React.ReactElement {
    const { value, schema, propertyKey } = props;

    return (
        <Picker
            label={schema.title || propertyKey}
            labelDescription={schema.description}
            value={value}
            onValueChange={props.onChangeValue}
            items={schema.enum.map((value) => ({
                label: value,
                value,
            }))}
        />
    );
}

function FormSchemaWithEnumCompletion(
    props: {
        propertyKey: string;
        schema: MandateProps<FormSchemaString, 'completion_url'>;
        value: string;
        globalValue: object;
        onChangeValue: (value: string) => void;
    } & MandateProps<FormSchemaOptionalProps, 'onFetchResource'>
): React.ReactElement {
    const { value, schema, propertyKey, globalValue } = props;
    const [completionItems, setCompletionItems] = React.useState<PickerItemData[] | null>(null);
    const [loading, wrapLoading] = useLoadingState(false);

    React.useEffect(() => {
        /**
         * Fetch only if there are no items yet otherwise it
         * will fetch everytime the `globalValue` changes.
         */
        if (!completionItems) {
            wrapLoading(async () => {
                try {
                    const items = await props.onFetchResource(schema.completion_url);
                    setCompletionItems(items);
                } catch (_err) {
                    setCompletionItems(null);
                }
            });
        }
    }, [globalValue]);

    const activeItem = completionItems?.find((item) => item.value === value);

    return (
        <FormButton label={schema.title || propertyKey} labelDescription={schema.description}>
            <PaletteButton
                size="medium"
                kind="secondary"
                disabled={loading || !completionItems}
                trailingIcon={loading ? undefined : Icon.ChevronDown}
                leadingIcon={loading ? LoadingIndicator : undefined}
                palette={() => {
                    return createPalette({
                        search: { placeholder: 'Select item...' },
                        groups: [
                            {
                                key: 'items',
                                commands: (completionItems || []).map((item) => ({
                                    key: item.value,
                                    title: item.label,
                                    trigger: () => {
                                        props.onChangeValue(item.value);
                                    },
                                })),
                            },
                        ],
                    });
                }}
                label={activeItem && !loading ? activeItem.label : ''}
            />
        </FormButton>
    );
}

function withDefaultValue<T>(
    value: T | undefined,
    schemaDefault: T | undefined,
    actualDefault: T
): T {
    if (typeof value !== 'undefined') {
        return value;
    }
    if (typeof schemaDefault !== 'undefined') {
        return schemaDefault;
    }

    return actualDefault;
}
